import { t } from 'i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { getSeriesUrlByDataKey } from '../../../domain/dashboardChart/dashboardChartUseCase';
import cn from '../../../utils/cn';

import styles from './ChartLine.module.css';

const CHART_HEIGHT = 280;

/**
 * Open the series URL when a legend item is clicked.
 * @param {{ dataKey?: string | number | Function }} payload - Recharts legend payload
 * @param {MappedChartSeries[]} series - Mapped series
 * @returns {void}
 */
function openLegendUrl(payload, series) {
  const dataKey = typeof payload?.dataKey === 'string' ? payload.dataKey : undefined;
  const url = getSeriesUrlByDataKey(series, dataKey);
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Line chart for dashboard (Recharts LineChart).
 * @param {object} props
 * @param {MappedChartRow[]} props.rows - Recharts rows
 * @param {MappedChartSeries[]} props.series - Series
 * @param {MappedChartReferenceLine[]} [props.referenceLines] - Horizontal reference lines
 * @param {MappedChartLegend | null} [props.legend] - Legend config, null to hide
 * @returns {JSX.Element}
 */
function ChartLine({
  rows,
  series,
  referenceLines = [],
  legend = null,
}) {
  if (!rows?.length || !series?.length) {
    return <p className={styles['chart-line-empty']}>{t('dashboard.emptyChart')}</p>;
  }

  return (
    <div
      className={cn([
        styles['chart-line'],
        legend?.layout === 'horizontal' && styles['chart-line-legend-horizontal'],
      ])}
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <LineChart
          data={rows}
          margin={{
            top: 8,
            right: 16,
            left: 0,
            bottom: 8,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--neutral-200)" />
          <XAxis dataKey="category" tick={{ fill: 'var(--neutral-600)', fontSize: 12 }} />
          <YAxis tick={{ fill: 'var(--neutral-600)', fontSize: 12 }} />
          <Tooltip />
          {legend ? (
            <Legend
              layout={legend.layout}
              align={legend.align}
              verticalAlign={legend.verticalAlign}
              itemSorter={null}
              onClick={(payload) => openLegendUrl(payload, series)}
            />
          ) : null}
          {series.map((serie) => (
            <Line
              key={serie.dataKey}
              type="monotone"
              dataKey={serie.dataKey}
              name={serie.name}
              stroke={serie.color}
              strokeWidth={serie.emphasis ? 3 : 2}
              dot={false}
              connectNulls={false}
            />
          ))}
          {referenceLines.map((line) => (
            <ReferenceLine
              key={`${line.label}-${line.value}`}
              y={line.value}
              stroke={line.color}
              strokeDasharray="4 4"
              label={{ value: line.label, fill: line.color, position: 'insideTopRight' }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default ChartLine;
